import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReservationForm from '../components/reservation/ReservationForm';
import PaymentSummary from '../components/payment/PaymentSummary';
import { useBookingStore } from '../stores/bookingStore';
import { ReservationFormData } from '../types';
import { setSEO } from '../utils/seo';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    startDateTime,
    endDateTime,
    selectedVehicle,
    setReservationData,
    reservationData,
  } = useBookingStore();

  const [isProcessing, setIsProcessing] = useState(false);

  // Set page title
  React.useEffect(() => {
    window.scrollTo(0, 0);
    setSEO('reservation');
  }, []);

  // Redirect if incomplete booking data
  React.useEffect(() => {
    if (!startDateTime || !endDateTime || !selectedVehicle) {
      navigate('/');
    }
  }, [startDateTime, endDateTime, selectedVehicle, navigate]);

  if (!startDateTime || !endDateTime || !selectedVehicle) {
    return null;
  }

  const handleFormSubmit = async (formData: ReservationFormData) => {
    setIsProcessing(true);

    try {
      // Save reservation data to store
      setReservationData(formData);

      // In a real app, create reservation via API first
      // const reservation = await reservationApi.createReservation({...});

      // Navigate to payment page
      navigate('/payment');
    } catch (error) {
      console.error('Checkout error:', error);
      alert(t('payment.checkoutError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate('/vehicle-detail', { state: { vehicle: selectedVehicle } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-500 shadow-md relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between relative">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors relative z-20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <div className="flex flex-col items-start">
                <span className="text-sm text-white/90 font-semibold">{t('flow.vehicleDetail')}</span>
              </div>
            </button>
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-3 pointer-events-none">
              <img src="/logo.svg" alt={t('brand.name')} className="h-8" />
              <h1 className="text-xl font-semibold text-white">{t('flow.checkoutTitle')}</h1>
            </div>
            <div className="w-20"></div>
          </div>

          {/* Mobile Header */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={handleBack}
              className="flex items-center text-white/90 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex items-center gap-2 flex-1">
              <img src="/logo.svg" alt={t('brand.name')} className="h-7" />
              <h1 className="text-lg font-semibold text-white truncate">{t('flow.reservationInfo')}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Progress Indicator - Desktop */}
          <div className="mb-8 hidden md:block">
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    ✓
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.dateSelect')}</span>
                </div>
                <div className="w-16 h-1 bg-primary-600 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    ✓
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.vehicleSelect')}</span>
                </div>
                <div className="w-16 h-1 bg-primary-600 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    3
                  </div>
                  <span className="ml-2 font-medium text-gray-900">{t('flow.reservationInfo')}</span>
                </div>
                <div className="w-16 h-1 bg-gray-300 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-semibold">
                    4
                  </div>
                  <span className="ml-2 font-medium text-gray-500">{t('flow.payment')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator - Mobile Simplified */}
          <div className="mb-4 md:hidden">
            <div className="flex items-center justify-center gap-1.5">
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  ✓
                </div>
                <span className="text-[10px] font-medium text-gray-700">{t('flow.date')}</span>
              </div>
              <div className="w-4 h-0.5 bg-primary-600"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  ✓
                </div>
                <span className="text-[10px] font-medium text-gray-700">{t('flow.vehicle')}</span>
              </div>
              <div className="w-4 h-0.5 bg-primary-600"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                  3
                </div>
                <span className="text-[10px] font-medium text-gray-900">{t('flow.reservation')}</span>
              </div>
              <div className="w-4 h-0.5 bg-gray-300"></div>
              <div className="flex items-center gap-0.5">
                <div className="w-5 h-5 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-[10px] font-bold">
                  4
                </div>
                <span className="text-[10px] font-medium text-gray-500">{t('flow.payment')}</span>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {/* Left Column - Reservation Form */}
            <div className="order-1 md:order-1">
              <ReservationForm
                onSubmit={handleFormSubmit}
                onChange={setReservationData}
                isLoading={isProcessing}
                initialData={reservationData}
              />
            </div>

            {/* Right Column - Payment Summary */}
            <div className="order-2 md:order-2 md:sticky md:top-4 h-fit">
              <PaymentSummary
                vehicle={selectedVehicle}
                startDateTime={startDateTime}
                endDateTime={endDateTime}
                userName={reservationData?.name || t('flow.enterReservationInfo')}
                userPhone={reservationData?.phone || "-"}
                pickupLocationInfo={reservationData?.pickupLocationInfo}
                returnLocationInfo={reservationData?.returnLocationInfo}
                selectedInsuranceId={reservationData?.selectedInsuranceId}
                selectedEquipmentIds={reservationData?.selectedEquipmentIds}
              />

              {/* Payment Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">{t('payment.paymentGuideTitle')}</p>
                    <p>{t('payment.paymentGuideDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
